import { getOrElse } from 'fp-ts/lib/Option';
import {
  StyledMovementLabelWrapper,
  StyledCircleLegend,
  StyledLegendItem,
  StyledCaptionPrimary,
  StyledCaptionSecondary,
} from 'modules/intelligentInsights/brandAffinityInsights/components/breakdownByAffinity/cohortTabContent/common.styles';
import {
  useBrandAffinityTranslation,
  BRAND_AFFINITY_NAMES_TO_I18N_KEYS,
  BrandAffinityLevel,
} from 'modules/domains/brandAffinity';
import { getOtherBrandAffinityCohorts } from 'modules/intelligentInsights/domain.utils';
import { useCoreContext } from 'modules/intelligentInsights/brandAffinityInsights/contexts/core/CoreContext';
import { localizePercent } from 'utils/l10n';
import {
  defaultCalculateMovementMetrics,
  useDataManagementContext,
} from 'modules/intelligentInsights/brandAffinityInsights/contexts/dataManagement/DataManagementContext';
import {
  BrandAffinityLevelNewUser,
  BrandAffinityLevelDeletedUser,
  BrandAffinityWithNewOrDeleted,
} from 'modules/intelligentInsights/brandAffinityInsights/types';
import {
  MOVEMENT_NEW_TO_PROJECT,
  MOVEMENT_DELETED_USERS,
} from 'modules/intelligentInsights/brandAffinityInsights/constants';
import { match } from 'ts-pattern';

export const calculateCohortMovement = (totalUsers: number, usersCount: number) => {
  if (totalUsers === 0) {
    return localizePercent(0);
  }

  return localizePercent(usersCount / totalUsers);
};

export const MovementLegend = ({
  legendType,
}: {
  legendType: 'newUsersCohort' | 'usersLeftCohort';
}) => {
  const { selectedAffinityTab } = useCoreContext();
  const { managedInsightsData } = useDataManagementContext();

  const allowedCohorts = getOtherBrandAffinityCohorts(selectedAffinityTab);
  const allCohortsWithAddedStatuses: Array<BrandAffinityWithNewOrDeleted> =
    legendType === 'newUsersCohort'
      ? [...allowedCohorts, BrandAffinityLevelNewUser]
      : [...allowedCohorts, BrandAffinityLevelDeletedUser];
  const calculatedMovementData = getOrElse(() => defaultCalculateMovementMetrics)(
    managedInsightsData.calculatedMovementMetrics,
  );

  const findMovementData = (selectedAffinity: BrandAffinityLevel) =>
    calculatedMovementData.filter((data) => {
      return data.cohort === selectedAffinity;
    })[0];

  const { t } = useBrandAffinityTranslation();

  return (
    <StyledMovementLabelWrapper>
      {allCohortsWithAddedStatuses.map((allowedCohortForLegend: BrandAffinityWithNewOrDeleted) => {
        const dataForCohort = findMovementData(selectedAffinityTab);
        const isNewUserCohort = legendType === 'newUsersCohort';

        const { text, usersCount } = match(allowedCohortForLegend)
          .with(BrandAffinityLevelNewUser, () => ({
            usersCount: dataForCohort.newUsersToProject,
            text: MOVEMENT_NEW_TO_PROJECT,
          }))
          .with(BrandAffinityLevelDeletedUser, () => ({
            usersCount: dataForCohort.deletedUsers,
            text: MOVEMENT_DELETED_USERS,
          }))
          .otherwise(() => {
            return {
              usersCount: isNewUserCohort
                ? (dataForCohort.newUsersCohort[
                    allowedCohortForLegend as BrandAffinityLevel
                  ] as number)
                : (dataForCohort.usersLeftCohort[
                    allowedCohortForLegend as BrandAffinityLevel
                  ] as number),
              text: t(
                BRAND_AFFINITY_NAMES_TO_I18N_KEYS[allowedCohortForLegend as BrandAffinityLevel],
              ),
            };
          });

        const legendMetric = isNewUserCohort
          ? calculateCohortMovement(dataForCohort.totalNewUsers, usersCount)
          : calculateCohortMovement(dataForCohort.totalUsersLeft, usersCount);

        return (
          <StyledLegendItem key={allowedCohortForLegend}>
            <StyledCircleLegend data-cohort={allowedCohortForLegend} />
            <StyledCaptionSecondary>{text}</StyledCaptionSecondary>
            <StyledCaptionPrimary>{`${legendMetric}`}</StyledCaptionPrimary>
          </StyledLegendItem>
        );
      })}
    </StyledMovementLabelWrapper>
  );
};
